import { Note } from '@components/ArtBoard/Block/Note'
import { Loading } from '@lib/components'
import { migrationProjectSelector } from '@store/migrationSelectors/projectSelectors'
import { clearSelectedBlocks } from '@storeOld/reducers/shared'
import cx from 'classnames'
import type React from 'react'
import { useRef, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { useClickAway } from 'react-use'

import { THEME_BOLD } from '@/constants/constants'
import type { AppState } from '@/types'
import { isInsidePopover } from '@/utils'
import lightOrDark from '@/utils/color/lightOrDark'
import { getDragAndDropAttributes } from '../DragAndDrop/getDragAndDropAttributes'
import useOnDragStart from '../DragAndDrop/hooks/useOnDragStart'
import { DRAGGABLE_TYPE } from '../DragAndDrop/types'
import css from './Block.module.scss'
import { BlockActionsToolbar } from './BlockActionsToolbar'
import { BlockVisual } from './BlockVisual'
import { Name } from './Name'

type Props = {
  id: string
  index: number
  nodeId?: string
  isOverlay?: boolean
  isFirst?: boolean
  isLast?: boolean
}

export const Block: React.FC<Props> = ({
  id,
  index = 0,
  isOverlay = false,
  isFirst = false,
  isLast = false,
}) => {
  const ref = useRef<HTMLDivElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const dispatch = useDispatch()

  // The wireframes popover of the toolbar is open: the rows of the block can
  // be dragged and removed meanwhile. The toolbar lives on the selected
  // block only, so a deselect closes it as well.
  const [wireframesOpen, setWireframesOpen] = useState(false)

  const block = useSelector(migrationProjectSelector.getProjectBlockById(id))

  // A block of a symbol deleted from the library: gone as soon as the client
  // learns it, the purge commit follows from whoever may write.
  const symbolMissing = useSelector(
    migrationProjectSelector.getProjectBlockSymbolMissing(id)
  )

  if (!block || symbolMissing) {
    return null
  }

  const {
    title: name,
    type,
    colorId,
    completed,
    visual,
  } = useSelector(
    migrationProjectSelector.getProjectBlockOrSymbolData(id),
    shallowEqual
  )

  const colorHex = useSelector(
    migrationProjectSelector.getProjectColorForUI(colorId)
  )

  const isSymbol = type === 'symbol'

  const isReadOnly = useSelector(
    migrationProjectSelector.getProjectLegacyAccessTypeIsReadOnly
  )

  const isDragging = useSelector<AppState, boolean>(
    ({ artboard }) =>
      artboard.dragAndDrop.dragged.id === id && artboard.dragAndDrop.isActive
  )

  // @todo translate
  // Блоков может быть выбрано несколько. isSelected считается тот, для которого
  // мы будем показывать тулбар. В процессе выбора блоков мышкой, такой блок будет последним.
  const isLastSelected = useSelector<AppState, boolean>(
    ({ canvas }) => !!id && canvas.block.ids[canvas.block.ids.length - 1] === id
  )

  // @todo translate
  // Блок у которого редактируется текстовое поле. Мы можем редактировать только одно текстовое поле одновременно.
  const isEditable = useSelector<AppState, boolean>(
    ({ canvas }) => canvas.block.selectedToEditBlockId === id
  )

  // @todo translate
  // Блок который подсвечен. Подсвечиваетя блок если он выбран, редактируется или если у он является одним из выбранных символов.
  const isHighlighted = useSelector<AppState, boolean>(
    ({ canvas }) => canvas.block.ids.includes(id)
    // @TODO review this. higlightind by selected symbol
    // || (isSymbol && canvas.symbol.selectedSymbol === symbolId)
  )

  const isImageUploading = useSelector<AppState, boolean>(
    ({ canvas }) =>
      canvas.uploadingImage.id === id && canvas.uploadingImage.uploading
  )

  const theme = useSelector(migrationProjectSelector.getProjectTheme)

  useClickAway(containerRef, ({ target }) => {
    if (isInsidePopover(target)) {
      return
    }

    // Fires on mousedown, while the name textarea may still be focused: the
    // blur that follows only ends editing, so the selection is cleared here
    // regardless of the editing state.
    // @todo review this
    // @ts-expect-error
    dispatch(clearSelectedBlocks())
  })

  // @todo refactor this
  // const previewColor = useSelector<AppState, string | null>(
  //   (state) => getPreviewColor(state, id),
  //   shallowEqual
  // )

  // const variableColor = resolveColor(previewColor || color)
  const variableColor = colorHex
  const themeBold = theme === THEME_BOLD

  const onDragStart = useOnDragStart(ref, DRAGGABLE_TYPE.BLOCK, id, {
    // @todo review this
    // symbolId,
    // isSymbol,
  })

  const blockStyles = {
    '--block-color': variableColor,
  } as React.CSSProperties

  // biome-ignore lint/plugin/no-jsx-in-function-variables: legacy pattern
  const BlockComponent = (
    <div className={css.wrapper}>
      <div
        ref={ref}
        className={cx(css.container, 'block', {
          [css.completed]: completed,
          [css.disabled]: isDragging && !isOverlay,
          [css.symbol]: isSymbol,
          [css.highlighted]: isHighlighted,
          [css.light]: lightOrDark(variableColor) === 'dark',
          [css.dark]: lightOrDark(variableColor) === 'light',
          [css.dragging]: isDragging,
          [css.overlay]: isOverlay,
          [css.last]: isLast,
          [css.first]: isFirst,
          [css.empty]: visual.type !== 'image' && !name,
          [css.bold_theme]: themeBold,
        })}
        style={blockStyles}
        {...getDragAndDropAttributes(id, DRAGGABLE_TYPE.BLOCK, {
          index,
        })}
        data-scroll-id={id}
      >
        {!isReadOnly && !isEditable && (
          <div className={css.draggableContainer} onPointerDown={onDragStart} />
        )}

        <Name blockId={id} completed={completed} themeBold={themeBold} />

        {!isImageUploading && (
          <BlockVisual
            imageClass={cx(css.image, 'block-image')}
            blockId={id}
            completed={completed}
            editable={wireframesOpen && !isReadOnly && !isOverlay}
          />
        )}

        {isImageUploading && (
          <div className={css.uploading}>
            <div>
              <Loading type="medium" color="gray" />
            </div>
          </div>
        )}

        <Note blockId={id} completed={completed} />
      </div>
    </div>
  )

  if (isLastSelected && !isOverlay) {
    return (
      <div ref={containerRef}>
        {BlockComponent}

        <BlockActionsToolbar
          anchorRef={ref}
          blockId={id}
          onWireframesOpenChange={setWireframesOpen}
          wireframesOpen={wireframesOpen}
        />
      </div>
    )
  }

  return BlockComponent
}
